import React, { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEVICE_SIZE } from '@marty-js/design/src/utils/mq';
import type { ItemSidebarComponentGlobalType, ItemSidebarKeyType, ItemSidebarType } from './types';
import { SideContentSpacer } from '../../atoms/grid-container';
import component from './component/componentList';

interface HTMLStickDivElement extends HTMLDivElement {
  stickyInfo?: {
    id?: string;
    position?: number;
    subElements?: HTMLDivElement[];
    originOffsetTop?: number;
    originMarginTop?: number;
    mode?: 'sticky' | 'margin' | 'sub' | null;
    init?: boolean;
  };
}

const isStickyElement = (elem: HTMLStickDivElement | HTMLDivElement, isSticky = false): elem is HTMLStickDivElement => {
  return isSticky || 'stickyInfo' in elem;
};

const STICKY_MARGIN = 10;
export const DEFAULT_MARGIN = 45;

type CallbackEventType = 'scroll' | 'resize';
type CallbackEvent = ((e: any) => void) | ResizeObserver | null;
type CallbackEvents = { [key in CallbackEventType]: CallbackEvent };
const hasWindow = typeof window !== 'undefined';
// add or replace callback event
export const addCallbackEvent = (
  element: (HTMLDivElement | HTMLElement) & { callbackEvents?: CallbackEvents },
  eventType: CallbackEventType,
  callbackEvent: CallbackEvent,
) => {
  if (!('callbackEvents' in element)) {
    element.callbackEvents = {} as CallbackEvents;
  }
  if ('callbackEvents' in element) {
    if (eventType === 'scroll') {
      if ('scroll' in element.callbackEvents && typeof element.callbackEvents.scroll === 'function') {
        window.removeEventListener('scroll', element.callbackEvents.scroll);
      }
      element.callbackEvents.scroll = callbackEvent;
      if (typeof element.callbackEvents.scroll === 'function') {
        window.addEventListener('scroll', element.callbackEvents.scroll);
      }
    } else if (eventType === 'resize') {
      if ('resize' in element.callbackEvents && element.callbackEvents.resize instanceof ResizeObserver) {
        // @ts-ignore
        element.callbackEvents.resize?.unobserve(element);
        delete element.callbackEvents.resize;
      }
      element.callbackEvents.resize = callbackEvent;
      if (callbackEvent instanceof ResizeObserver) {
        callbackEvent.observe(element);
      }
    }
  }
};

const heightWithMargin = (elem: HTMLDivElement): number => elem.offsetHeight + DEFAULT_MARGIN;

const switchSticky = (stickyElement: HTMLStickDivElement, spaceSticky: number, originOffset: number) => {
  const offsetMax =
    spaceSticky * stickyElement.stickyInfo.position +
    stickyElement.stickyInfo.originMarginTop +
    originOffset +
    stickyElement.stickyInfo.originOffsetTop;

  if (
    stickyElement.stickyInfo.mode !== 'margin' &&
    (stickyElement.offsetTop > offsetMax || stickyElement.getBoundingClientRect().top < STICKY_MARGIN)
  ) {
    // de-sticky & add margin
    stickyElement.stickyInfo.mode = 'margin';
    stickyElement.style.position = null;
    stickyElement.style.marginTop = `${spaceSticky + stickyElement.stickyInfo.originMarginTop}px`;

    stickyElement.stickyInfo.subElements.forEach((elem) => {
      elem.style.position = null;
      if (isStickyElement(elem)) {
        elem.stickyInfo.mode = null;
      }
    });
  } else if (
    stickyElement.stickyInfo.mode !== 'sticky' &&
    stickyElement.stickyInfo.mode !== 'sub' &&
    stickyElement.getBoundingClientRect().top > STICKY_MARGIN
  ) {
    // sticky
    stickyElement.stickyInfo.mode = 'sticky';
    stickyElement.style.position = 'sticky';
    stickyElement.style.top = `${STICKY_MARGIN}px`;
    stickyElement.style.marginTop = null;

    let sumComponentHeight = heightWithMargin(stickyElement);

    stickyElement.stickyInfo.subElements.forEach((elem) => {
      elem.style.position = 'sticky';
      elem.style.top = `${sumComponentHeight + STICKY_MARGIN}px`;
      sumComponentHeight += heightWithMargin(elem);
      if (isStickyElement(elem)) {
        elem.stickyInfo.mode = 'sub';
      }
    });
  }
};

const initSticky = (stickyElement: HTMLStickDivElement, spaceSticky: number, originOffset: number) => {
  let nextSticky: HTMLStickDivElement = null;
  switchSticky(stickyElement, spaceSticky, originOffset);
  addCallbackEvent(stickyElement, 'scroll', () => {
    switchSticky(stickyElement, spaceSticky, originOffset);
  });
  stickyElement.stickyInfo.subElements.forEach((subElem) => {
    if (!nextSticky && isStickyElement(subElem)) {
      nextSticky = subElem;
      nextSticky.stickyInfo.subElements = [];
    } else if (nextSticky) {
      nextSticky.stickyInfo.subElements.push(subElem);
    }
  });
  if (nextSticky && !nextSticky.stickyInfo.init) {
    initSticky(nextSticky, spaceSticky, originOffset);
  }
};

const resetElement = (elem: HTMLDivElement | HTMLStickDivElement) => {
  if ('stickyInfo' in elem) {
    delete elem.stickyInfo;
  }
  elem.removeAttribute('style');
  addCallbackEvent(elem, 'scroll', null);
};
type WidgetProps = ItemSidebarComponentGlobalType & {
  setIsSticky: (key: string, val: boolean) => void;
  componentRef: MutableRefObject<HTMLDivElement>;
  id: string;
};
const Widget: React.FC<WidgetProps> = ({ template, data, setIsSticky, isFirst, componentRef, id }) => {
  const setIsIamSticky = useMemo(() => (val: boolean) => setIsSticky(id, val), [setIsSticky, id]);

  if (template in component) {
    const Component = component[template];

    return (
      <SideContentSpacer ref={componentRef} id={id}>
        <Component template={template} data={data} setIsIamSticky={setIsIamSticky} isFirst={isFirst} />
      </SideContentSpacer>
    );
  }

  return null;
};

const ItemSidebar: React.FC<ItemSidebarType> = ({ components, refContainer }) => {
  const [spaceAvailable, setSpaceAvailable] = useState<number>(null);
  const isStickies: { [key in ItemSidebarKeyType]?: [boolean, (val: boolean) => void] } = useMemo(() => ({}), []);
  isStickies.sidebarPos1 = useState<boolean>(false);
  isStickies.sidebarPos2 = useState<boolean>(false);
  isStickies.sidebarPos3 = useState<boolean>(false);
  isStickies.sidebarPos4 = useState<boolean>(false);
  isStickies.sidebarPos5 = useState<boolean>(false);
  isStickies.sidebarPos6 = useState<boolean>(false);

  const componentRefs: { [key in ItemSidebarKeyType]?: MutableRefObject<HTMLDivElement | HTMLStickDivElement> } =
    useMemo(() => ({}), []);

  componentRefs.sidebarPos1 = useRef<HTMLDivElement>();
  componentRefs.sidebarPos2 = useRef<HTMLDivElement>();
  componentRefs.sidebarPos3 = useRef<HTMLDivElement>();
  componentRefs.sidebarPos4 = useRef<HTMLDivElement>();
  componentRefs.sidebarPos5 = useRef<HTMLDivElement>();
  componentRefs.sidebarPos6 = useRef<HTMLDivElement>();

  const resizeCallback = useCallback(() => {
    const container = refContainer.current;
    if (!container) {
      return;
    }
    let sumComponentHeight = 0;
    Object.keys(componentRefs).forEach((key: ItemSidebarKeyType) => {
      const curElement = componentRefs[key].current;
      if (curElement) {
        sumComponentHeight += heightWithMargin(curElement);
      }
    });

    const newSpaceAvailable = container?.offsetHeight - sumComponentHeight;
    if (newSpaceAvailable !== spaceAvailable) {
      setSpaceAvailable(newSpaceAvailable);
    }
  }, [componentRefs, spaceAvailable, setSpaceAvailable, refContainer]);

  const resizeObserver = useMemo(
    () =>
      hasWindow
        ? new ResizeObserver(() => {
            resizeCallback();
          })
        : null,
    [resizeCallback],
  );

  useEffect(() => {
    if (window.innerWidth < DEVICE_SIZE.LARGE) {
      return;
    }
    const container = refContainer.current;
    addCallbackEvent(container, 'resize', resizeObserver);
    let firstSticky: HTMLStickDivElement = null;
    let sumComponentHeight = 0;
    let countSticky = 0;

    // component under first Sticky
    const subElements: HTMLDivElement[] = [];
    Object.keys(isStickies).forEach((key: ItemSidebarKeyType) => {
      const curElement = componentRefs[key].current;
      if (curElement) {
        resetElement(curElement);
        addCallbackEvent(curElement, 'resize', resizeObserver);
        if (firstSticky) {
          subElements.push(curElement);
        }
        if (isStickyElement(curElement, isStickies[key][0])) {
          countSticky += 1;
          curElement.stickyInfo = {};
          curElement.stickyInfo.position = countSticky;
          curElement.stickyInfo.originOffsetTop = sumComponentHeight;
          curElement.stickyInfo.originMarginTop = DEFAULT_MARGIN;
          curElement.stickyInfo.id = key;
          curElement.stickyInfo.mode = null;
          if (!firstSticky) {
            firstSticky = curElement;
          }
        }

        sumComponentHeight += heightWithMargin(curElement);
      }
    });

    if (sumComponentHeight && countSticky) {
      const newSpaceAvailable = container.offsetHeight - sumComponentHeight;
      if (newSpaceAvailable !== spaceAvailable) {
        setSpaceAvailable(newSpaceAvailable);
      }
      const spaceBySticky = (newSpaceAvailable - STICKY_MARGIN) / countSticky;
      // check is space for sticky & init
      if (firstSticky && spaceBySticky > 0) {
        firstSticky.stickyInfo.subElements = subElements;
        initSticky(firstSticky, spaceBySticky, container.offsetTop);
      }
    }
  }, [
    isStickies,
    isStickies.sidebarPos1,
    isStickies.sidebarPos2,
    isStickies.sidebarPos3,
    isStickies.sidebarPos4,
    isStickies.sidebarPos5,
    isStickies.sidebarPos6,
    componentRefs,
    refContainer,
    spaceAvailable,
    setSpaceAvailable,
    resizeObserver,
  ]);

  const setIsSticky = useCallback(
    (key: ItemSidebarKeyType, val: boolean) => {
      if (isStickies[key][0] !== val) {
        isStickies[key][1](val);
      }
    },
    [isStickies],
  );

  return (
    <>
      {Object.keys(isStickies).map((key: ItemSidebarKeyType) => {
        const widgetData: ItemSidebarComponentGlobalType = components[key] as ItemSidebarComponentGlobalType;

        if (widgetData) {
          return (
            <Widget
              key={key}
              componentRef={componentRefs[key]}
              id={key}
              {...widgetData}
              setIsSticky={setIsSticky}
              isFirst={key === 'sidebarPos1'}
            />
          );
        }

        return null;
      })}
    </>
  );
};

export default ItemSidebar;
